import axios from "axios";
import { getCookie } from "containers/Pages/Chat/utils/cookiesHelper";
import qs from "qs";
import DateUtils from "./date";
import UrlUtils from "./url";

const mapPrices = (product) => ({
  ...product,
  prices: product.prices || { min_price: product.price, max_price: null },
});

export class RequestGroup {
  constructor(cancellable = true) {
    if (cancellable) {
      this.setCancelToken();
    }
  }

  setCancelToken = () => {
    this.cancelTokenSource = axios.CancelToken.source();
    this.cancelToken = this.cancelTokenSource.token;
  };

  cancelAndReuse = (message) => {
    this.cancelTokenSource.cancel(message);
    this.setCancelToken();
  };

  notCancelError = (error) => !axios.isCancel(error);

  getActiveSubscriptions = async (dateRange) => {
    const url = UrlUtils.getProperApiUrl("/subscription/latest");
    const params = dateRange ? { dateRange } : {};
    const { data } = await axios.get(url, {
      params,
      cancelToken: this.cancelToken,
    });
    return data;
  };

  getListOrganizations = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/admin/list_organizations"),
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": getCookie("X-User-Token"),
        },
      }
    );
    return data;
  };

  getOrgDomains = async (orgId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/admin/organization/domains"),
      {
        cancelToken: this.cancelToken,
        params: { org_id: orgId },
      }
    );
    return data;
  };

  deleteOrgDomain = async (id, orgId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/admin/organization/domain"),
      {
        cancelToken: this.cancelToken,
        params: { id, org_id: orgId },
      }
    );
    return data;
  };

  deleteOrganization = async (orgId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/organization/${orgId}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getOrgDataByPName = async (orgName) => {
    const response = await axios.get(
      UrlUtils.getProperApiUrl("/organization", true) + "?p_name=" + orgName,
      {
        cancelToken: this.cancelToken,
      }
    );
    return response.data;
  };

  getOrgDataById = async (orgId, countryId) => {
    const params = { org_id: orgId };
    if (countryId != null) params.country_id = countryId;
    const response = await axios.get(
      UrlUtils.getProperApiUrl("/organization", true),
      {
        params,
        cancelToken: this.cancelToken,
      }
    );
    // const response = await axios.get(
    //   `https://api.dev.meetusvr.com/navbox/organization?org_id=${orgId}`,
    //   { cancelToken: this.cancelToken }
    // );
    return response.data;
  };

  getBrands = async (orgId) => {
    try {
      const { data } = await axios.get(
        UrlUtils.getProperApiUrl("/organization/brands"),
        {
          params: { org_id: orgId },
          cancelToken: this.cancelToken,
        }
      );
      return data.content ?? data;
    } catch (e) {
      console.log(e);
      return [];
    }
  };

  getOrgThemes = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/themes"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCategories = async (orgId) => {
    const response = await axios.get(
      UrlUtils.getProperApiUrl("/categories", true) +
        (orgId ? `?org_id=${Number(orgId)}` : ""),
      { cancelToken: this.cancelToken }
    );
    return response.data;
  };

  getTags = async (orgId, categoryName, countryId) => {
    try {
      const { data } = await axios.get(
        UrlUtils.getProperApiUrl("/tags", true),
        {
          params: {
            org_id: orgId,
            category_name: categoryName,
            ...(countryId != null ? { country_id: countryId } : {}),
          },
          cancelToken: this.cancelToken,
        }
      );
      return (data.content ?? data).map((tag) => {
        let metadata;
        try {
          metadata = JSON.parse(tag.metadata) || {};
        } catch {
          metadata = {};
        }
        return {
          ...tag,
          metadata,
        };
      });
    } catch (e) {
      console.log(e);
    }
  };

  getTagsPaginated = async (orgId, start, count, countryId, search) => {
    const trimmedSearch = (search || "").trim();
    try {
      const { data } = await axios.get(
        UrlUtils.getProperApiUrl("/tags", true),
        {
          params: {
            org_id: orgId,
            start,
            count,
            ...(countryId ? { country_id: countryId } : {}),
            ...(trimmedSearch ? { search: trimmedSearch } : {}),
          },
          cancelToken: this.cancelToken,
        }
      );
      return {
        ...data,
        content: data.content.map((tag) => {
          let metadata;
          try {
            metadata = JSON.parse(tag.metadata) || {};
          } catch {
            metadata = {};
          }
          return {
            ...tag,
            metadata,
          };
        }),
      };
    } catch (e) {
      console.log(e);
    }
  };

  getTag = async (tagId, countryId) => {
    try {
      const { data } = await axios.get(UrlUtils.getProperApiUrl("/tag", true), {
        params: {
          tag_id: tagId,
          ...(countryId ? { country_id: countryId } : {}),
        },
        cancelToken: this.cancelToken,
      });
      data.metadata = JSON.parse(data.metadata);
      return data;
    } catch (e) {
      console.log(e);
    }
  };

  postTagCountryCover = async ({ tagId, countryId, organizationImageId }) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/organization/tag"),
      {
        operation: "update",
        id: tagId,
        image_country_id: countryId,
        organization_image_id: organizationImageId,
      },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getThemesClasses = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/admin/themes/class"),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  deleteThemesClass = async (id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/admin/themes/class"),
      { params: { id }, cancelToken: this.cancelToken }
    );
    return data;
  };

  getThemes = async (classId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/admin/themes"),
      { cancelToken: this.cancelToken, params: { class_id: classId } }
    );
    return data;
  };

  deleteTheme = async (uid) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/admin/themes"),
      { cancelToken: this.cancelToken, params: { id: uid } }
    );
    return data;
  };

  getOrganizationThemesClasses = async (orgId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/themes/class"),
      { cancelToken: this.cancelToken, params: { org_id: orgId } }
    );
    return data;
  };

  getTagsTree = async (orgId, countryId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/tagstree", true),
      {
        params: {
          ...(orgId ? { org_id: Number(orgId) } : {}),
          ...(countryId != null ? { country_id: Number(countryId) } : {}),
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteTag = async (tagId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/organization/tag") +
        `?tag_id=${Number(tagId)}`,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  postProductsTags = async (productsIds, tagsIds) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/product/tag"),
      { products_ids: productsIds, tags_ids: tagsIds },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  deleteProductsTags = async (productsIds, tagsIds) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/product/tag"),
      {
        params: { products_ids: productsIds, tags_ids: tagsIds },
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteProductImage = async (imageId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/product/image"),
      {
        params: { image_id: imageId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getBrand = async (brandId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/brand", true) + `?brand_id=${Number(brandId)}`,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  deleteBrand = async (brandId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/organization/brand") +
        `?brand_id=${Number(brandId)}`,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getCountries = async (orgId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/countries", true),
      { params: { org_id: orgId }, cancelToken: this.cancelToken }
    );
    return data;
  };

  getSubAreas = async ({ countryId, cityId, areaId } = {}) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/sub_areas"),
      {
        params: {
          country_id: countryId,
          city_id: cityId,
          area_id: areaId,
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postSubAreas = async (subAreas) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/organization/sub_areas"),
      { sub_areas: subAreas },
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteSubAreas = async (subAreas) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/organization/sub_areas"),
      {
        params: {
          sub_areas: subAreas,
        },
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getShop = async (shopId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/shop", true) + `?shop_id=${Number(shopId)}`,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getShops = async (start = 0, count = 100) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/shops"),
      { params: { start, count }, cancelToken: this.cancelToken }
    );
    return data;
  };

  getShopMapping = async (orgId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/integration/dictionary"),
      {
        params: {
          dict_type: "SHOP",
          org_id: orgId,
          page_num: 0,
          page_size: 12,
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  mapShop = async (orgId, localValue, remoteValue) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/integration/mapping"),
      {
        org_id: orgId,
        mapping_type: "SHOP",
        local_value: String(localValue),
        remote_value: remoteValue,
      },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getProductsFeaturesTypes = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/products_features/types"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getProducts = async (filters) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/products", true),
      {
        params: filters,
        cancelToken: this.cancelToken,
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
      }
    );
    return {
      ...data,
      products: data.products.map((product) => mapPrices(product)),
    };
  };

  postBulkProductTranslations = async (productTranslations) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/product/v2/add-product-translations"),
      productTranslations,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postConversionTranslation = async (translationData) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/translate/multi"),
      translationData,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getEmptyProducts = async (orgId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/product/empty_products"),
      {
        params: { org_id: orgId },
        cancelToken: this.cancelToken,
      }
    );
    return Array.isArray(data) ? { total: data.length, products: data } : data;
  };

  getFilters = async (filters) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/filters", true),
      {
        params: filters,
        cancelToken: this.cancelToken,
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
      }
    );
    return data;
  };

  getProduct = async (productId) => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/product"), {
      params: { product_id: productId },
      cancelToken: this.cancelToken,
    });
    return {
      ...data,
      variants: data.variants || [],
      images: data.images || [],
    };
  };

  getUsersCarts = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/users/carts"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getRelatedProducts = async (productId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/related_products", true),
      {
        params: { product_id: productId },
        cancelToken: this.cancelToken,
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
      }
    );
    const { products, total } = Array.isArray(data)
      ? { products: data, total: data.length }
      : data;
    return {
      products: products.map((product) => mapPrices(product)),
      total,
    };
  };

  postRelatedProducts = async (add, productId, relatedProductsIds) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/product/related_products"),
      {
        add,
        product_id: productId,
        related_products_ids: relatedProductsIds,
      },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getVariants = async (filters) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/variants", true),
      {
        params: filters,
        cancelToken: this.cancelToken,
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
      }
    );
    return data;
  };

  getEmptyCollections = async (orgId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/product/empty_collections"),
      {
        params: { org_id: orgId },
        cancelToken: this.cancelToken,
      }
    );
    return Array.isArray(data)
      ? {
        total: data.length,
        products: data.map((product) => mapPrices(product)),
      }
      : data;
  };

  getCollection = async (productId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/collection", true),
      {
        params: { id: productId },
        cancelToken: this.cancelToken,
      }
    );
    return {
      ...data,
      variants: data.variants || [],
      images: data.images || [],
    };
  };

  deleteCollection = async (collectionId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/product/collection"),
      {
        params: { id: collectionId },
      }
    );
    return data;
  };

  postCollectionElement = async (operation, productId, variantIds) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/product/collection/element"),
      {
        operation,
        product_id: productId,
        variant_ids: variantIds,
      },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  deleteProduct = async (productIds, forceDeleteCollectionItems) => {
    const { data } = await axios.delete(UrlUtils.getProperApiUrl("/product"), {
      params: {
        product_id: productIds,
        force_delete_collection_items: forceDeleteCollectionItems,
      },
      paramsSerializer: (params) =>
        qs.stringify(params, {
          arrayFormat: "repeat",
          skipNulls: true,
        }),
    });
    return data;
  };

  hideProduct = async (productId, hide = true) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/product/hide"),
      null,
      {
        params: { product_id: productId, hide },
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  get360Products = async (shopId, name) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/360view/products"),
      {
        params: { shop_id: shopId, name },
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: false,
          }),
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  set360Search = async (include, productIds) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/product/set_360_search"),
      null,
      {
        params: { include, product_id: productIds },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getOrderList = async (query) => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/order/list"), {
      params: query,
      cancelToken: this.cancelToken,
      paramsSerializer: (params) =>
        qs.stringify(params, {
          arrayFormat: "repeat",
          skipNulls: true,
          encoder: (str, defaultEncoder, charset, type) => {
            const encoded = defaultEncoder(str, defaultEncoder, charset);
            return type === "value" ? encoded.replace(/%2C/gi, ",") : encoded;
          },
        }),
    });
    return data;
  };

  getReturnedOrdersList = async (query) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/order/return/requests"),
      {
        params: query,
        cancelToken: this.cancelToken,
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
      }
    );
    return data;
  };

  getOrderInfo = async (orderId) => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/order/info"), {
      params: { order_id: orderId },
      cancelToken: this.cancelToken,
    });
    return data;
  };

  getMetaOrderInfo = async (metaOrderId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/order/meta_order/info"),
      {
        params: { id: metaOrderId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postOrderStatusUpdate = async (orderId, status) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/order/status/update"),
      {
        order_id: orderId,
        status,
      },
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postOrderConfirm = async (orderId) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/order/confirm"),
      null,
      {
        params: { order_id: orderId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getReturnRequests = async ({
    metaOrderId,
    shopId,
    status,
    start,
    count,
    dateFrom,
    dateTo,
  } = {}) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/order/return/requests"),
      {
        params: {
          meta_order_id: metaOrderId,
          shop_id: shopId,
          status,
          start,
          count,
          date_from: dateFrom
            ? DateUtils.toISOStringWithoutMillis(dateFrom)
            : null,
          date_to: dateTo ? DateUtils.toISOStringWithoutMillis(dateTo) : null,
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getReturnRequest = async (returnRequestId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/order/return/request"),
      {
        params: { id: returnRequestId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postRejectOrderRequest = async (orderId, reason) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/order/reject"),
      {
        sub_order_id: orderId,
        rejection_reason: reason,
      },
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postRejectReturnOrderRequest = async (returnOrderId, reason) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/order/return/reject"),
      {
        return_request_id: returnOrderId,
        rejection_reason: reason,
      },
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postConfirmReturnRequest = async (returnRequestId) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/order/return/confirm"),
      null,
      {
        params: { id: returnRequestId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postReturnRequestReceive = async (receivedItemsData) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/order/return/received_item"),
      receivedItemsData,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getUserList = async (role, orgId, shopId) => {
    const token = getCookie("X-User-Token");
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/user/list"), {
      params: { role, org_id: orgId, shop_id: shopId },
      cancelToken: this.cancelToken,
      headers: {
        "User-Token": token,
      },
    });
    return data;
  };

  deleteUser = async (employeeId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/user/admin/employee/${employeeId}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  restoreUser = async (employeeId) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl(`/user/admin/employee/${employeeId}/restore`),
      {},
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCompensationType = async (params) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/action"),
      {
        params,
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCompensationRule = async (params) => {
    console.log("params for rules", params);
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(
        `/compensation/rule/filter?compensationActionIds=${params}`
      ),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getActiveRules = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/rule/list"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCustomerList = async (params) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/user/list/customer"),
      {
        params,
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getUserInfo = async (userId, employee = true) => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/user/info"), {
      params: { id: userId, is_employee: employee },
      cancelToken: this.cancelToken,
    });
    return data;
  };

  getChatRooms = async () => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/chat/rooms"), {
      cancelToken: this.cancelToken,
      skipAuthLogout: true,
    });
    return data;
  };

  getChatIdByUser = async (userId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/chat/user/${userId}`),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getChatMessages = async (chatId, { page, size, direction } = {}) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/message/by-chat/${chatId}`),
      {
        params: { page: page || 0, size: size || 50, direction: direction || "DESC" },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getUser = async () => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/user/info"), {
      cancelToken: this.cancelToken,
    });
    return data;
  };

  setDeviceToken = async (deviceToken) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/user/notification-token"),
      deviceToken,
      {
        headers: {
          "Content-Type": "text/plain",
        },
      }
    );
    return data;
  };

  postUSerSuspend = async (userId, employee, suspend) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/user/suspend"),
      null,
      {
        params: { user_id: userId, is_employee: employee, suspend },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getProductListCsvTemplate = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/upload/productlist/csv/template"),
      { cancelToken: this.cancelToken, responseType: "blob" }
    );
    return data;
  };

  getProductListExcelTemplate = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/upload/productlist/xls/template"),
      { cancelToken: this.cancelToken, responseType: "blob" }
    );
    return data;
  };

  getExportProductsCsv = async (shopId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/export/products/csv"),
      {
        params: { shop_id: shopId },
        cancelToken: this.cancelToken,
        responseType: "blob",
      }
    );
    return data;
  };

  getExportProductsExcel = async (shopId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/export/products/xlsx"),
      {
        params: { shop_id: shopId },
        cancelToken: this.cancelToken,
        responseType: "blob",
      }
    );
    return data;
  };

  postProductFeature = async (productsFeature) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/organization/products_feature"),
      productsFeature,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getProductFeatures = async (orgId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/products_features"),
      {
        params: { organization_id: orgId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteProductFeature = async (id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/organization/products_feature"),
      {
        params: { id },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getImageBulkTemplate = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/product/image/bulk/template"),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getOrgExtraAttributes = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/extra_attribute"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  get360Sections = async (shopId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/360view/sections?shop_id=${shopId}`),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  post360Sections = async (shopId, floors) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl(`/360view/sections?shop_id=${shopId}`),
      floors,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  publish360 = async (shopId) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl(`/360view/publish?shop_id=${shopId}`),
      null,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  postFile = async (file, orgId, cancelToken) => {
    const formData = new FormData();
    formData.append("file", file);
    if (orgId) formData.append("org_id", orgId);
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/files"),
      formData,
      {
        cancelToken: this.cancelToken || cancelToken,
      }
    );
    return data;
  };

  deleteFile = async (fileUrl) => {
    const { data } = axios.delete(UrlUtils.getProperApiUrl("/files"), {
      params: { file_name: fileUrl },
      cancelToken: this.cancelToken,
    });
    return data;
  };

  postOrgImage = async (file, properties, onUploadProgress, cancelToken) => {
    const formData = new FormData();
    formData.append("image", file);
    formData.append("properties", JSON.stringify(properties));
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/organization/image"),
      formData,
      { cancelToken: this.cancelToken || cancelToken, onUploadProgress }
    );
    return data;
  };

  deleteOrganizationImage = async (imageUrl, imageId = null) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/organization/image"),
      {
        params: { image_id: imageId, url: imageUrl },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  post360viewShop = async (shopId) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/360view/shops"),
      { shop_id: shopId },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  // in case of conflict with # 61,
  // accept this as the api would be changed from /360view/sections to /360view/floors
  delete360Floors = async (shopId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/360view/floors") + `?shop_id=${shopId}`,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  delete360Floor = async (
    shopId,
    floorId,
    productsPositionsConfirm = false
  ) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/360view/floor"),
      {
        params: {
          shop_id: shopId,
          floor_id: floorId,
          products_positions_confirm: productsPositionsConfirm,
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  delete360Section = async (
    shopId,
    sectionId,
    productsPositionsConfirm = false
  ) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/360view/section"),
      {
        params: {
          shop_id: shopId,
          section_id: sectionId,
          products_positions_confirm: productsPositionsConfirm,
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  delete360Scene = async (
    shopId,
    SceneId,
    productsPositionsConfirm = false
  ) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/360view/scene"),
      {
        params: {
          shop_id: shopId,
          scene_id: SceneId,
          products_positions_confirm: productsPositionsConfirm,
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getPromotions = async (params = {}) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/promotions"),
      {
        params,
        cancelToken: this.cancelToken,
      }
    );
    let promotions = Array.isArray(data) ? data : data.promotions;
    promotions = promotions.map((promotion) => ({
      ...promotion,
      name: promotion.name || promotion.identifier,
    }));
    return { ...data, promotions };
  };

  postPromotion = async (promotion) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/organization/promotion"),
      promotion,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deletePromotion = async (promoId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/organization/promotion"),
      {
        params: { id: promoId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  // events methods
  getAllEventsList = async (query) => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/event/list"), {
      params: query,
      cancelToken: this.cancelToken,
      paramsSerializer: (params) =>
        qs.stringify(params, {
          arrayFormat: "repeat",
          skipNulls: true,
        }),
    });
    return data;
  };

  getRoomEventsList = async (orgId, query) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/room/event/list"),
      {
        params: {
          org_id: orgId,
          status: query.status,
          start: query.start,
          count: query.count,
        },
        cancelToken: this.cancelToken,
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
      }
    );
    return data;
  };

  getEvent = async (eventId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/event/${eventId}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getEventInterests = async (eventId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/event/interests/${eventId}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteEvent = async (eventId, force = false) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/event/${eventId}?force=${force}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  // influencers methods
  getInfluencerById = async (influencerId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/influencer/${influencerId}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getInfluencerEventsById = async (influencerId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/influencer/events"),
      { params: { influencerId }, cancelToken: this.cancelToken }
    );
    return data;
  };

  getInfluencersEventsRequests = async (start, count, status) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/influencer/hosting-requests"),
      {
        params: { start, count, status },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  // For You
  getSharedItems = async (start, count) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/post/orgSharedProducts"),
      {
        params: { start, count },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getReviewsRequests = async (start, count, postStatus) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/post/orgReviews"),
      {
        params: { start, count, postStatus },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  updatePostReviewRequestStatus = async (postId, postStatus) => {
    const { data } = await axios.put(
      UrlUtils.getProperApiUrl("/post/approve"),
      null,
      {
        params: { postId, postStatus },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  approveOrRejectHostRequest = async (requestId, approve) => {
    const { data } = await axios.put(
      UrlUtils.getProperApiUrl(`/influencer/host/${requestId}`),
      null,
      {
        params: { approve },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getInfluencerEventRequestDetails = async (requestId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/influencer/request/${requestId}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  suspendOrAcceptInfluencerRequest = async (influencerId, action) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/influencer/response"),
      null,
      {
        params: { influencerId, action },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAllInfluencers = async (start, count, status) => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/influencer"), {
      params: { start, count, status },
      cancelToken: this.cancelToken,
    });
    return data;
  };

  emailUsersWithPromotion = async (promo, userIds) => {
    const res = await axios.post(
      UrlUtils.getProperApiUrl("/mail/cart/abandoned"),
      { promo, user_ids: userIds },
      { cancelToken: this.cancelToken }
    );
    return res.data;
  };

  getProductReview = async (start = 0, count = 12) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("product/review"),
      { params: { start, count }, cancelToken: this.cancelToken }
    );
    return data;
  };

  postProductReviewApprove = async (id) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/product/review/approve"),
      null,
      { params: { id }, cancelToken: this.cancelToken }
    );
    return data;
  };

  getSubscribedUsers = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/subscribed_users"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteSubscribedUser = async (email) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/organization/subscribed_users"),
      {
        params: { email },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getSubscriptionInfo = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/subscription/info"),
      {
        cancelToken: this.cancelToken,
        skipAuthLogout: true,
        headers: {
          Authorization: `Bearer ${getCookie("X-User-Token")}`,
          "User-Token": getCookie("X-User-Token"),
        },
      }
    );
    return data;
  };

  cancelSubscription = async (subscriptionId) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/subscription/cancel"),
      null,
      {
        params: { subscriptionId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getOrganizationSiteMap = async (
    url,
    includeProducts,
    includeCollections,
    includeBrands,
    includeTags,
    includeTagsTree
  ) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/sitemap", true),
      {
        params: {
          url,
          include_products: includeProducts,
          include_collections: includeCollections,
          include_brands: includeBrands,
          include_tags: includeTags,
          include_tags_tree: includeTagsTree,
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getNavboxSeo = async (orgId, type, id) => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/seo", true), {
      params: {
        org_id: orgId,
        type,
        id,
      },
      cancelToken: this.cancelToken,
    });
    return data;
  };

  postOrganizationSeo = async (type, id, keywords) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/organization/seo"),
      {
        type,
        id,
        keywords,
      },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getStatisticsCarts = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/carts"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getStatisticsSoldProducts = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/sold_products"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getStatisticsUsers = async (month) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/users"),
      {
        params: { month },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCustomerStatistics = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/customers"),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getStatisticsOrders = async (statuses, monthCount = 12, type = "TOTAL") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/orders"),
      {
        params: { statuses, months_count: monthCount, type },
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getStatisticsSales = async (month, week) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/sales"),
      {
        params: { month, week },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAnalyticsTraffic = async (period = "month") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/analytics/traffic"),
      {
        params: { period },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAnalyticsTouchpoints = async (period = "month") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/analytics/touchpoints"),
      {
        params: { period },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAnalyticsConversion = async (period = "month") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/analytics/conversion"),
      {
        params: { period },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAnalyticsReferrers = async (period = "month") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/analytics/referrers"),
      {
        params: { period },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAnalyticsDevices = async (period = "month") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/analytics/devices"),
      {
        params: { period },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAnalyticsCountries = async (period = "month") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/analytics/countries"),
      {
        params: { period },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAnalyticsFrequency = async (period = "month") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/analytics/frequency"),
      {
        params: { period },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAnalyticsExitPages = async (period = "month") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/analytics/exit-pages"),
      {
        params: { period },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAnalyticsVisitorsLive = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/analytics/visitors/live"),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getVisitorHistory = async (visitorId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/statistics/analytics/visitors/${encodeURIComponent(visitorId)}/history`),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getPageAnalytics = async (period = "month") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/analytics/pages"),
      {
        params: { period },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCampaigns = async (period = "month") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/analytics/campaigns"),
      {
        params: { period },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postAIChat = async (message, history = []) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/statistics/analytics/ai-chat"),
      { message, history },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  sendBroadcastNotification = async (title, body) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/notification/broadcast"),
      { title, body },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getWalletFunnelStats = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/wallet/funnel/stats"),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getCategoriesList = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/yeshtery/categories"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteCategory = async (categoryId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/admin/category") +
        `?category_id=${Number(categoryId)}`,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getSessions = async (params) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/videochat/sessions"),
      {
        params,
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postJoinSession = async (orgId, sessionName) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/videochat/session"),
      null,
      {
        params: { org_id: orgId, session_name: sessionName },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postLeaveSession = async (orgId, sessionName, endCall) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/videochat/leave"),
      null,
      {
        params: {
          org_id: orgId,
          session_name: sessionName,
          end_call: endCall,
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteVariant = async (varianttIds, forceDeleteCollectionItems) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/product/variant"),
      {
        params: {
          variant_id: varianttIds,
          force_delete_collection_items: forceDeleteCollectionItems,
        },
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
      }
    );
    return data;
  };

  getOrdersFilter = async (query) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/order/filters") +
        UrlUtils.toSearchParams(query),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  postExport360Images = async (shopId) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/360view/export_images"),
      null,
      {
        params: { shop_id: shopId },
        responseType: "blob",
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getTagsByFilter = async (orgId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/navbox/filters"),
      {
        params: { org_id: orgId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getProductsInChatCategories = async ({ orgId, tags }) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/navbox/products"),
      {
        params: { org_id: orgId, tag_ids: tags },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteShop = async (shopId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/shop/delete") + `?shop_id=${Number(shopId)}`,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getAvailability = async (orgId, employeeId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/availability/org/${orgId}/${employeeId}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteAvailability = async (starts_at, ends_at, force = false) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/availability"),
      {
        params: { starts_at, ends_at, force },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAdvertisementsList = async (start, count, orgId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/advertisement"),
      {
        params: { start, count, orgId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  // Loyalty (points)
  getLoyaltyPointsByUser = async (userId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/loyalty/points/list_by_user"),
      {
        params: { user_id: userId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAwardsByUser = async (userId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/loyalty/awards/list_by_user"),
      {
        params: { user_id: userId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getTiersList = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/loyalty/tier/list"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getWalletPass = async (tierId, orgId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(
        `/organization/loyalty-wallet/organization/tier/${tierId}`
      ),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getDefaultColors = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/themes"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postWalletPass = async (tierId, formData, config = {}) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl(`/organization/loyalty-wallet/tier/${tierId}`),
      formData,
      {
        cancelToken: this.cancelToken,
        headers:
          formData instanceof FormData
            ? { "Content-Type": "multipart/form-data" }
            : { "Content-Type": "application/json" },
        ...config,
      }
    );
    return data;
  };

  updateWalletPass = async (tierId, formData, config = {}) => {
    const { data } = await axios.put(
      UrlUtils.getProperApiUrl(`/organization/loyalty-wallet/tier/${tierId}`),
      formData,
      {
        cancelToken: this.cancelToken,
        headers:
          formData instanceof FormData
            ? { "Content-Type": "multipart/form-data" }
            : { "Content-Type": "application/json" },
        ...config,
      }
    );
    return data;
  };

  sendWalletPassNotification = async (payload) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/wallet/notification/send"),
      payload,
      {
        cancelToken: this.cancelToken,
        headers: {
          Authorization: `Bearer ${getCookie("X-User-Token")}`,
        },
      }
    );
    return data;
  };

  sendBankTransferByEmail = async (receiverEmail, amount) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl(`/bank/transaction/in/email?receiverEmail=${encodeURIComponent(receiverEmail)}&amount=${amount}`),
      null,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getWalletPassNotificationStats = async (tierId) => {
    const params = tierId ? `?tierId=${tierId}` : "";
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/wallet/notification/stats${params}`),
      {
        cancelToken: this.cancelToken,
        headers: {
          Authorization: `Bearer ${getCookie("X-User-Token")}`,
        },
      }
    );
    return data;
  };

  getWalletDownloadsByShop = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/wallet/downloads/by-shop"),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getWalletUserStatus = async (userId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/wallet/user-status?userId=${userId}`),
      {
        cancelToken: this.cancelToken,
        headers: {
          Authorization: `Bearer ${getCookie("X-User-Token")}`,
        },
      }
    );
    return data;
  };

  getWalletNotificationHistory = async (userId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/wallet/notification/history?userId=${userId}`),
      {
        cancelToken: this.cancelToken,
        headers: {
          Authorization: `Bearer ${getCookie("X-User-Token")}`,
        },
      }
    );
    return data;
  };

  getWalletAppleQrCode = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/wallet/apple/qrCode"),
      { cancelToken: this.cancelToken, responseType: "blob" }
    );
    return data;
  };

  getWalletGoogleQrCode = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/wallet/google/qrCode"),
      { cancelToken: this.cancelToken, responseType: "blob" }
    );
    return data;
  };

  getWalletActivePromotions = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/wallet/promotions/active"),
      {
        cancelToken: this.cancelToken,
        headers: {
          Authorization: `Bearer ${getCookie("X-User-Token")}`,
        },
      }
    );
    return data;
  };

  // Rewards Catalog CRUD
  getRewardsCatalog = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/rewards/catalog"),
      {
        cancelToken: this.cancelToken,
        headers: {
          Authorization: `Bearer ${getCookie("X-User-Token")}`,
        },
      }
    );
    return data;
  };

  createRewardsCatalogItem = async (item) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/rewards/catalog"),
      item,
      {
        headers: {
          Authorization: `Bearer ${getCookie("X-User-Token")}`,
        },
      }
    );
    return data;
  };

  updateRewardsCatalogItem = async (itemId, item) => {
    const { data } = await axios.put(
      UrlUtils.getProperApiUrl(`/rewards/catalog/${itemId}`),
      item,
      {
        headers: {
          Authorization: `Bearer ${getCookie("X-User-Token")}`,
        },
      }
    );
    return data;
  };

  deleteRewardsCatalogItem = async (itemId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/rewards/catalog/${itemId}`),
      {
        headers: {
          Authorization: `Bearer ${getCookie("X-User-Token")}`,
        },
      }
    );
    return data;
  };

  // Wallet Analytics
  getWalletAnalyticsOverview = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/wallet/analytics/overview"),
      {
        cancelToken: this.cancelToken,
        headers: {
          Authorization: `Bearer ${getCookie("X-User-Token")}`,
        },
      }
    );
    return data;
  };

  getConfigList = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/loyalty/config/list"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteConfig = async (id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/loyalty/config/delete?id=${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getReferralLink = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("organization/referral/signup/url"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  updateReferralLink = async (newLink) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/organization/referral/signup/url"),
      {
        referralSignUpUrl: newLink
      },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getShopCheckInRewardConfig = async (shopId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/shop_check_in/organization/config/shop/${shopId}`),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  createShopCheckInRewardConfig = async (shopId, points, eligibilityDateRange) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl(`/shop_check_in/organization/config/shop/${shopId}`),
      { points, eligibilityDateRange: eligibilityDateRange || null },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  updateShopCheckInRewardConfig = async (shopId, points, eligibilityDateRange) => {
    const { data } = await axios.put(
      UrlUtils.getProperApiUrl(`/shop_check_in/organization/config/shop/${shopId}`),
      { points, eligibilityDateRange: eligibilityDateRange || null },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getShopCheckInQR = async (shopId, shopUrlPage = UrlUtils.getProperApiUrl("/shop_check_in/reward")) => {
    const response = await axios.get(
      UrlUtils.getProperApiUrl("/shop_check_in/reward/qrCode"),
      {
        params: { shopId, shopUrlPage },
        cancelToken: this.cancelToken,
        responseType: "blob",
      }
    );
    return response.data;
  };

  deleteAdvertisement = async (id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/advertisement/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAdvertisement = async (id) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/advertisement/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getTier = async (tier_id) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/loyalty/tier"),
      {
        params: { tier_id },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteTier = async (tier_id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/loyalty/tier/delete"),
      {
        params: { tier_id },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getShippingServices = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/shipping/service"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteShippingService = async (service_id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/organization/shipping/service"),
      {
        params: { service_id },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getDeliveryCoverageArea = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(
        "/organization/settings/cart_optimization/strategy"
      ),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  acceptOrRejectQueue = async (queueId, requestType, rejectionReason) => {
    const { data } = await axios.put(
      UrlUtils.getProperApiUrl(`/queue/${requestType}`),
      null,
      {
        params: {
          queueId,
          ...(rejectionReason && { rejectionReason }),
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getOutOfStocks = async (start, count, org_id) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/product/out-of-stock-products"),
      {
        params: { start, count, org_id },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteProductFromCart = async ({ productId, userId }) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl("/cart/item"),
      {
        cancelToken: this.cancelToken,
        params: { item_id: productId, user_id: userId },
      }
    );
    return data;
  };

  applyPromoCode = async (promo, userId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/cart/${userId}`),
      {
        params: { promo },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postCheckout = async (sessionId, paymentMethod, options = {}) => {
    const token = getCookie("X-User-Token");
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/cart/store-checkout/initiate"),
      {
        sessionId,
        paymentMethod,
        ...options,
      },
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": token,
        },
      }
    );
    return data;
  };

  executePaymentCod = async (order_id) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/payment/cod/execute"),
      null,
      {
        params: { order_id },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  loginAgentToChat = async () => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/chat/agent/authenticate")
    );
    return data;
  };

  getChatPerformanceSummary = async (orgId, from, to) => {
    try {
      const { data } = await axios.post(
        UrlUtils.getProperApiUrl("/customerService/Analytics/performance/summary"),
        { organizationId: orgId, from, to },
        { cancelToken: this.cancelToken, skipAuthLogout: true }
      );
      return data;
    } catch (err) {
      // Fallback: assemble from individual count endpoints
      const body = { organizationId: orgId, from, to };
      const [convResp, senderCounts, escalated] = await Promise.all([
        axios.get(
          UrlUtils.getProperApiUrl(`/customerService/Analytics/count/conversations?organization_id=${orgId}`),
          { cancelToken: this.cancelToken, skipAuthLogout: true }
        ).then((r) => r.data).catch(() => 0),
        axios.post(
          UrlUtils.getProperApiUrl("/customerService/Analytics/count/Messages"),
          body,
          { cancelToken: this.cancelToken, skipAuthLogout: true }
        ).then((r) => r.data).catch(() => []),
        axios.post(
          UrlUtils.getProperApiUrl("/customerService/Analytics/count/escalate/Messages"),
          body,
          { cancelToken: this.cancelToken, skipAuthLogout: true }
        ).then((r) => r.data).catch(() => 0),
      ]);

      let botMessages = 0;
      let agentMessages = 0;
      let customerMessages = 0;
      if (Array.isArray(senderCounts)) {
        senderCounts.forEach((sc) => {
          if (sc.senderType === "BOT") botMessages = sc.messageCount || 0;
          if (sc.senderType === "AGENT") agentMessages = sc.messageCount || 0;
          if (sc.senderType === "USER") customerMessages = sc.messageCount || 0;
        });
      }
      const totalMessages = botMessages + agentMessages + customerMessages;

      return {
        totalConversations: convResp || 0,
        avgResponseTimeMinutes: 0,
        escalationRate: totalMessages > 0 ? (escalated * 100.0) / totalMessages : 0,
        aiHandlingRate: totalMessages > 0 ? (botMessages * 100.0) / totalMessages : 0,
        totalMessages,
        botMessages,
        agentMessages,
        customerMessages,
        escalatedMessages: escalated || 0,
      };
    }
  };

  getChatDailyVolume = async (orgId, from, to) => {
    try {
      const { data } = await axios.post(
        UrlUtils.getProperApiUrl("/customerService/Analytics/performance/daily"),
        { organizationId: orgId, from, to },
        { cancelToken: this.cancelToken, skipAuthLogout: true }
      );
      return data;
    } catch (err) {
      return [];
    }
  };

  getChatAgentPerformance = async (orgId, from, to) => {
    try {
      const { data } = await axios.post(
        UrlUtils.getProperApiUrl("/customerService/Analytics/performance/agents"),
        { organizationId: orgId, from, to },
        { cancelToken: this.cancelToken, skipAuthLogout: true }
      );
      return data;
    } catch (err) {
      // Fallback: extract agent data from chat rooms
      try {
        const rooms = await this.getChatRooms();
        const agentMap = {};
        (rooms?.items || rooms || []).forEach((room) => {
          const sender = room?.latestMessage?.sender;
          if (sender?.agent && sender?.id) {
            if (!agentMap[sender.id]) {
              agentMap[sender.id] = {
                agentId: sender.id,
                agentName: sender.name || "Agent",
                agentImage: sender.image,
                conversations: 0,
                avgResponseTimeMinutes: 0,
                messageCount: 0,
                escalatedCount: 0,
              };
            }
            agentMap[sender.id].conversations += 1;
          }
        });
        return Object.values(agentMap).sort((a, b) => b.conversations - a.conversations);
      } catch (e) {
        return [];
      }
    }
  };

  getBankAccount = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/bank/account"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getBankAccountSummary = async (userId) => {
    const url = userId
      ? `/bank/account/summary/${userId}`
      : "/bank/account/summary";
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(url),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  depositCoins = async (txHash) => {
    if (!txHash || typeof txHash !== "string" || !txHash.startsWith("0x")) {
      throw new Error("Invalid transaction hash");
    }
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/bank/transaction/deposit"),
      { txHash },
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  wertAutoDeposit = async (txHash) => {
    if (!txHash || typeof txHash !== "string" || !txHash.startsWith("0x")) {
      throw new Error("Invalid transaction hash");
    }
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl(`/bank/transaction/wert-deposit?txHash=${encodeURIComponent(txHash)}`),
      null,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getDepositAddress = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/bank/bc/deposit-address"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  wertOrderStatus = async (clickId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/bank/wert-order-status?clickId=${encodeURIComponent(clickId)}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getBankAccountHistory = async (start = 0, count = 10, type = "", ruleId = "") => {
    let url = `/bank/account/history?start=${start}&count=${count}`;
    if (type) url += `&type=${type}`;
    if (ruleId) url += `&ruleId=${ruleId}`;
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(url),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getBankReservations = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/bank/account/reservations"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCompensationFailedPayouts = async (start = 0, count = 10) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/compensation/failed-payouts?start=${start}&count=${count}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getReconciliationStatus = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/bank/reconciliation/status"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getVideoCallStatistics = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/video_call"),
      {
        cancelToken: this.cancelToken,
      }
    );

    return data;
  };

  getWebScraping = async (orgId, start, count, type) => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/scraping"), {
      params: { orgId, start, count, type },
      cancelToken: this.cancelToken,
    });
    return data;
  };

  deleteWebScraping = async (id) => {
    const { data } = await axios.delete(UrlUtils.getProperApiUrl("/scraping"), {
      params: { id },
      cancelToken: this.cancelToken,
    });
    return data;
  };

  postWebScrapingFile = async (file, orgId, manualCollect, bootName) => {
    const formData = new FormData();
    if (file) {
      formData.append("file", file);
    }
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/scraping/file"),
      file ? formData : null,
      {
        params: { orgId, manualCollect, bootName },
        cancelToken: this.cancelToken || cancelToken,
      }
    );
    return data;
  };

  postKnowledgeBaseFile = async (file, orgId) => {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/scraping/file"),
      formData,
      {
        params: { orgId, manualCollect: true, bootName: "knowledge_base" },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  activateAI = async (orgId) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/scraping/activate"),
      null,
      {
        params: { orgId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  // Web Scraping Progress API methods
  getActiveScrapingJobs = async (orgId) => {
    try {
      const { data } = await axios.get(
        UrlUtils.getProperApiUrl("/scraping/jobs/active"),
        {
          params: { orgId },
          cancelToken: this.cancelToken,
        }
      );
      return data;
    } catch (error) {
      // Endpoint might not exist, return empty array
      return [];
    }
  };

  getScrapingJobStatus = async (jobId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/scraping/jobs/${jobId}`),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  cancelScrapingJob = async (jobId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/scraping/jobs/${jobId}`),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  // Subscribe to SSE progress stream
  subscribeToScrapingProgress = (jobId, onProgress, onComplete, onError) => {
    const token = localStorage.getItem("X-User-Token") || "";
    const url = `${UrlUtils.getProperApiUrl(`/scraping/jobs/${jobId}/stream`)}?token=${encodeURIComponent(token)}`;

    const eventSource = new EventSource(url);

    eventSource.addEventListener("progress", (event) => {
      try {
        const data = JSON.parse(event.data);
        if (onProgress) onProgress(data);
      } catch (err) {
        console.error("Failed to parse progress event:", err);
      }
    });

    eventSource.addEventListener("complete", (event) => {
      try {
        const data = JSON.parse(event.data);
        if (onComplete) onComplete(data);
        eventSource.close();
      } catch (err) {
        console.error("Failed to parse complete event:", err);
      }
    });

    // Fallback for generic messages
    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (onProgress) onProgress(data);
        if (data.status === "COMPLETED" || data.status === "FAILED" || data.status === "CANCELLED") {
          if (onComplete) onComplete(data);
          eventSource.close();
        }
      } catch (err) {
        console.error("Failed to parse SSE event:", err);
      }
    };

    eventSource.onerror = (error) => {
      console.error("SSE connection error:", error);
      if (onError) onError(error);
      eventSource.close();
    };

    return eventSource;
  };

  getAskForEventList = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/event/list/advertise")
    );
    return data;
  };

  getUserCart = async (userId, isStoreCheckout = false) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/cart/${userId}`),
      {
        params: { isStoreCheckout },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  estimateTokensToUsdc = async (amount, currency) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/cart/estimate-tokens-to-usdc"),
      { amount, currency },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  initiateStoreCheckout = async (sessionId, paymentMethod, options = {}) => {
    const token = getCookie("X-User-Token");
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/cart/store-checkout/initiate"),
      {
        sessionId,
        paymentMethod,
        ...options,
      },
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": token,
        },
      }
    );
    return data;
  };

  getUserWishList = async (userId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/wishlist/${userId}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAllPromoCodesActive = async (orgId, typeId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(
        `/active_promotions?org_ids=${orgId}&type_ids=${typeId}`,
        true
      ),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  // Compensation Start

  getCompensationActions = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/action"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCompensationActionTypes = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/action/types"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  postCompensationRule = async (rule) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/compensation/rule"),
      rule,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCompensationActionById = async (id) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/compensation/action/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCompensationEligible = async (start, count) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/eligible/all"),
      {
        params: { start, count },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCompensationRulesPaginated = async (start, count) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/rule/all"),
      {
        params: { start, count },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCompensationRulesList = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/rule/all"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCompensationRuleById = async (id) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/compensation/rule/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteCompensationRuleById = async (id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/compensation/rule/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  editCompensationRuleById = async (id, rule) => {
    const { data } = await axios.put(
      UrlUtils.getProperApiUrl(`/compensation/rule/${id}`),
      rule,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCompensationCampaign = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/campaigns"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  // Get actions by campaign type
  // GET /compensation/actions/by-campaign?campaign=LOYALTY
  // Returns: ["COMPENSATION_ORDER_ONLINE", "COMPENSATION_FIRST_ORDER", ...]
  getActionsByCampaign = async (campaign) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/actions/by-campaign"),
      {
        params: { campaign },
        cancelToken: this.cancelToken
      }
    );
    return data;
  };

  // Get required params for a specific action
  // GET /compensation/additional-params?action=COMPENSATION_ORDER_ONLINE
  getAdditionalParams = async (action) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/additional-params"),
      { params: { action }, cancelToken: this.cancelToken }
    );
    return data;
  };

  getCompensationAction = async ({ action }) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/action/types"),
      {
        params: { campaign: action },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getCompensationChannelsByAction = async ({ action }) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/compensation/channels/by-action"),
      {
        params: { action },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  // Quest Engine - Promo Code Validation
  validateQuestPromoCode = async (code, orderAmount) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/api/v1/quest-engine/promo"),
      {
        params: { code, orderAmount },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  // Compensation End

  getProductsByIds = async (ids) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/products_by_ids", true),
      {
        params: { ids },
        cancelToken: this.cancelToken,
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: "repeat",
            skipNulls: true,
          }),
      }
    );

    return data;
  };

  // completeStoreCheckout = async (params) => {
  //   const { data } = await axios.post(
  //     UrlUtils.getProperApiUrl("/store-checkout/complete")
  //   );

  //   return data;
  // };

  completeStoreCheckout = async (params) => {
    const token = getCookie("X-User-Token");
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("cart/store-checkout/complete"),
      params,
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": token,
        },
      }
    );
    return data;
  };

  get3dProducts = async (params) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/product/model3d/all"),
      {
        params,
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  get3dProductDetails = async (barcode, sku) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/product/model3d/one"),
      {
        params: { barcode, sku },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  delete3dProduct = async (modelId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/product/model3d/${Number(modelId)}`),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  unAssign3dModel = async (params) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/product/model3d/unassign"),
      null,
      {
        params,
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteFileByUrl = async (fileUrl) => {
    const { data } = axios.delete(UrlUtils.getProperApiUrl("/files"), {
      params: { url: fileUrl },
      cancelToken: this.cancelToken,
    });
    return data;
  };

  getUserFollowing = async (start, count, followerId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/follow/following"),
      {
        params: { start, count, followerId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getUserFollower = async (start, count, userId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/follow/follower"),
      {
        params: { start, count, userId },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  userCheckout = async (bodyRequest) => {
    const token = getCookie("X-User-Token");
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/cart/store-checkout/complete"),
      bodyRequest,
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": token,
        },
      }
    );
    return data;
  };

  initiateCheckoutByOtp = async (sessionId, paymentMethod, options = {}) => {
    const token = getCookie("X-User-Token");
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/cart/store-checkout/initiate"),
      {
        sessionId,
        paymentMethod,
        ...options,
      },
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": token,
        },
      }
    );
    return data;
  };

  wertWidgetGenerateSignature = async (amount, account) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/bank/bc/wert-sc-data"),
      {
        receiverAddress: account,
        usdcAmount: amount * 10 ** 18,
      }
    );
    console.log("data from wert", data);
    return data;
  };

  getBrandsPaginated = async (orgId, start, count) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/organization/brands"),
      {
        params: { org_id: orgId, start, count },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getServices = async () => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/service"), {
      cancelToken: this.cancelToken,
    });
    return data;
  };

  getAllModules = async () => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/module/all"), {
      cancelToken: this.cancelToken,
    });
    return data;
  };

  getAllRoles = async () => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/roles"), {
      cancelToken: this.cancelToken,
    });
    return data;
  };

  getGlobalRolePermissions = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/roles/global/permissions"),
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": getCookie("X-User-Token"),
        },
      }
    );
    return data;
  };

  getOrganizationRolePermissions = async (orgId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/roles/organization/${orgId}/permissions`),
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": getCookie("X-User-Token"),
        },
      }
    );
    return data;
  };

  updateGlobalRolePermissions = async (roleId, permissionIds) => {
    const { data } = await axios.put(
      UrlUtils.getProperApiUrl(`/roles/global/role/${roleId}/permissions`),
      { permissionIds },
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": getCookie("X-User-Token"),
        },
      }
    );
    return data;
  };

  updateOrganizationRolePermissions = async (orgId, roleId, permissionIds) => {
    const { data } = await axios.put(
      UrlUtils.getProperApiUrl(`/roles/organization/${orgId}/role/${roleId}/permissions`),
      { permissionIds },
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": getCookie("X-User-Token"),
        },
      }
    );
    return data;
  };

  deleteRole = async (id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/roles/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  // updateRole = async (id, data) => {
  //   const { data } = await axios.put(UrlUtils.getProperApiUrl(`/roles/${id}`), data, {
  //     cancelToken: this.cancelToken,
  //   });
  //   return data;
  // };

  deleteModule = async (id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/module/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getPermissionIds = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/permission/id"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getPermissions = async () => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/permission"), {
      cancelToken: this.cancelToken,
    });
    return data;
  };

  // Endpoint → permission catalog, grouped by feature tag.
  getPermissionEndpoints = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/permission/endpoints"),
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": getCookie("X-User-Token"),
        },
      }
    );
    return data;
  };

  createPermission = async (name) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/permission"),
      { name },
      {
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": getCookie("X-User-Token"),
        },
      }
    );
    return data;
  };

  // getAllModules = async () => {
  //   const { data } = await axios.get("http://192.168.0.11:8060/v1/module/all")
  //   return data;
  // };
  getServiceUsage = async () => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/service/usage"), {
      cancelToken: this.cancelToken,
    });
    return data;
  };

  getServiceById = async (id) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/service/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteService = async (id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/service/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  duplicateService = async (id) => {
    const serviceData = await this.getServiceById(id);
    const { id: serviceId, ...serviceDataWithoutId } = serviceData;
    const duplicatedServiceData = {
      ...serviceDataWithoutId,
      name: `${serviceDataWithoutId.name} (Copy)`,
      code: `${serviceDataWithoutId.code} (Copy)`,
    };

    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/service"),
      duplicatedServiceData,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getPackages = async () => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/package"), {
      cancelToken: this.cancelToken,
    });
    return data;
  };

  getUsageStrategies = async () => {
    const { data } = await axios.get(UrlUtils.getProperApiUrl("/usage-strategies/metadata"), {
      cancelToken: this.cancelToken,
    });
    return data;
  };

  getPackageOrgs = async (packageId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/package/organizations/${packageId}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deletePackage = async (id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/package/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getAddOn = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/service/all-available-addons"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data.content;
  };

  getAllAvailableAddOn = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/service/available-addons"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  CreateAddOn = async (addOnData) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/service-addon"),
      addOnData,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  updateAddOn = async (id, addOnData) => {
    const { data } = await axios.put(
      UrlUtils.getProperApiUrl(`/service-addon/${id}`),
      addOnData,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteAddOn = async (id) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/service-addon/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  assignPackageToOrg = async (id, orgsIds) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/package/register"),
      {
        package_id: id,
        organization_ids: orgsIds,
      },
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getPendingOrder = async (id) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/order/pending-order/${id}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getSceneAll = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/scene/findAll"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  // Gift Card Utils
  getGiftCardThemes = async (page = 0, size = 20, search, isActive) => {
    const params = { page, size };
    if (search) params.search = search;
    if (isActive !== undefined && isActive !== null) params.isActive = isActive;
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/giftcards/themes/admin"),
      {
        params,
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  deleteGiftCardTheme = async (themeId) => {
    const { data } = await axios.delete(
      UrlUtils.getProperApiUrl(`/giftcards/themes/${themeId}`),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  createGiftCardTheme = async (formDataPayload) => {
    const formData = new FormData();

    // Convert formDataPayload object to FormData
    Object.keys(formDataPayload).forEach((key) => {
      const value = formDataPayload[key];

      // Handle File objects (for image uploads)
      if (value instanceof File || value instanceof Blob) {
        formData.append(key, value);
      } else if (Array.isArray(value)) {
        // Handle arrays
        // Special-case currencies: send as single JSON field so backend receives an array
        if (key === "currencies") {
          // Append currencies as repeated fields so backend parsers receive an array of strings
          value.forEach((item) => {
            formData.append(`${key}[]`, item);
          });
        } else {
          // Default behavior for arrays (legacy): append indexed entries
          value.forEach((item, index) => {
            formData.append(`${key}[${index}]`, item);
          });
        }
      } else if (typeof value === "object" && value !== null) {
        // Handle objects (stringify them)
        formData.append(key, JSON.stringify(value));
      } else {
        // Handle primitive values (string, number, boolean)
        formData.append(key, value);
      }
    });

    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/giftcards/themes"),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  updateGiftCardTheme = async (themeId, formDataPayload) => {
    const formData = new FormData();

    // Convert formDataPayload object to FormData
    Object.keys(formDataPayload).forEach((key) => {
      const value = formDataPayload[key];

      // Handle File objects (for image uploads)
      if (value instanceof File || value instanceof Blob) {
        formData.append(key, value);
      } else if (Array.isArray(value)) {
        // Handle arrays
        // Special-case currencies: send as single JSON field so backend receives an array
        if (key === "currencies") {
          // Append currencies as repeated fields so backend parsers receive an array of strings
          value.forEach((item) => {
            formData.append(`${key}[]`, item);
          });
        } else {
          value.forEach((item, index) => {
            formData.append(`${key}[${index}]`, item);
          });
        }
      } else if (typeof value === "object" && value !== null) {
        // Handle objects (stringify them)
        formData.append(key, JSON.stringify(value));
      } else {
        // Handle primitive values (string, number, boolean)
        formData.append(key, value);
      }
    });

    const { data } = await axios.put(
      UrlUtils.getProperApiUrl(`/giftcards/themes/${themeId}`),
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  issueGiftCard = async (formData) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/giftcards"),
      formData,
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getRecentSignUps = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/user/recent-signups"),
      {
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getShopperActivity = async ({ action, reference_type, page, size } = {}) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/user-activity"),
      {
        params: { action, reference_type, page, size },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getShopperActivityByUser = async (userId, { action, reference_type, page, size } = {}) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/user-activity"),
      {
        params: { user_id: userId, action, reference_type, page, size },
        cancelToken: this.cancelToken,
      }
    );
    return data;
  };

  getUserActivityActions = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/user-activity/action-types"),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  getUserActivityReferenceTypes = async () => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/user-activity/reference-types"),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  // Customer Cohort Analytics
  getCustomerCohorts = async (cohortBoundaries = "30,90,180,365") => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/statistics/customer-cohorts"),
      {
        params: { cohort_boundaries: cohortBoundaries },
        cancelToken: this.cancelToken,
        headers: {
          "User-Token": getCookie("X-User-Token"),
        },
      }
    );
    return data;
  };

  getCustomerPoints = async (userQrToken) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/bank/redemption/customer/${encodeURIComponent(userQrToken)}`),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  redeemPoints = async (token, pointsToRedeem) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl("/bank/redemption/redeem"),
      { token, points_to_redeem: pointsToRedeem },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  // View a scanned gift card (admin / ADMIN_VIEW_ALL_GIFT_CARDS authorized).
  getGiftCard = async (giftCardId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/giftcards/${encodeURIComponent(giftCardId)}`),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  // Redeem a gift card in store at face value (no loyalty points awarded).
  // Backend responds 200 with an empty body on success.
  redeemGiftCardInStore = async (giftCardId) => {
    await axios.post(
      UrlUtils.getProperApiUrl("/giftcards/redeem-in-store"),
      { giftCardId },
      { cancelToken: this.cancelToken }
    );
    return true;
  };

  getGiftcardOrders = async ({ senderEmail, recipientEmail, orderId, status, scheduledDateFrom, scheduledDateTo, page = 0, size = 20 } = {}) => {
    const params = { page, size };
    if (senderEmail) params.senderEmail = senderEmail;
    if (recipientEmail) params.recipientEmail = recipientEmail;
    if (orderId) params.orderId = orderId;
    if (status) params.status = status;
    if (scheduledDateFrom) params.scheduledDateFrom = scheduledDateFrom;
    if (scheduledDateTo) params.scheduledDateTo = scheduledDateTo;
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl("/giftcards/admin/search"),
      { params, cancelToken: this.cancelToken }
    );
    return data;
  };

  updateGiftcardOrderStatus = async (orderId, newStatus, transactionId) => {
    const { data } = await axios.put(
      UrlUtils.getProperApiUrl(`/giftcards/${orderId}/status`),
      { newStatus, transactionId },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  resendGiftcardOrderEmail = async (orderId) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl(`/giftcards/admin/${orderId}/resend-order-email`),
      null,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  reScheduleGiftCard = async (giftcardId, scheduledDate) => {
    const { data } = await axios.patch(
      UrlUtils.getProperApiUrl(`/giftcards/admin/${giftcardId}/schedule`),
      { scheduledDate },
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  sendGiftCardNow = async (giftCardId) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl(`/giftcards/admin/${giftCardId}/send-now`),
      null,
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  cancelGiftcardOrder = async (orderId) => {
    await axios.delete(
      UrlUtils.getProperApiUrl(`/giftcards/orders/${orderId}`),
      { cancelToken: this.cancelToken }
    );
  };

  // Bulk send progress for an order — drives the progress bar.
  // Returns { giftCardOrderId, total, sent, failed, scheduled, pending }.
  getGiftcardSendProgress = async (orderId) => {
    const { data } = await axios.get(
      UrlUtils.getProperApiUrl(`/giftcards/${orderId}/send-progress`),
      { cancelToken: this.cancelToken }
    );
    return data;
  };

  // Reset every EMAIL_FAILED card in the order back to PENDING_SEND (admin).
  // Returns the updated send-progress payload (same shape as getGiftcardSendProgress).
  resendGiftcardFailedCards = async (orderId) => {
    const { data } = await axios.post(
      UrlUtils.getProperApiUrl(`/giftcards/admin/${orderId}/resend-failed`),
      null,
      { cancelToken: this.cancelToken }
    );
    return data;
  };
}

const DefaultRequests = new RequestGroup(false);

export const getOldImagesCleaner =
  (imagesToCleanup, isOranizationImage = false) =>
    (oldData, newData, submittedValues = {}) => {
      imagesToCleanup.forEach(({ propertyName, fieldName }) => {
        if (
          oldData[propertyName] &&
        fieldName in submittedValues &&
        oldData[propertyName] !== newData[propertyName]
        ) {
          isOranizationImage
            ? DefaultRequests.deleteOrganizationImage(
              undefined,
              oldData[propertyName]
            )
            : DefaultRequests.deleteFile(oldData[propertyName]);
        }
      });
    };

export default {
  ...DefaultRequests,
};
